/** Report-only compatibility aliases. Never apply these to saved generation scenes. */
const DASHSCOPE_USAGE_ALIASES = new Map<string, string>([
  ["wan.27-r2v-2026-06-12", "wan2.7-r2v-2026-06-12"],
  ["wan2.6-r2v", "wan2.6-r2v-flash"],
]);

// Explicit product decision: treat the short-name historical logs as Wan, ignoring Veo POC.
// Their video_duration is the already-rounded REQUEST length, not process time or a group total.
export const historicalWan26ModelOf = (
  provider: string | null | undefined,
  model: string | null | undefined,
  videoDuration: number | null | undefined
): string | undefined => {
  if (provider?.toLowerCase() !== "dashscope" || model?.toLowerCase() !== "wan2.6" ||
    typeof videoDuration !== "number" || !Number.isFinite(videoDuration) || videoDuration <= 0) return undefined;
  return videoDuration <= 10 ? "wan2.6-r2v-flash" : "wan2.6-i2v";
};

export const canonicalUsageModel = (
  provider: string | null | undefined,
  model: string | null | undefined,
  videoDuration?: number | null
): string => {
  const raw = model || ""; // Legacy API logs may have a NULL model.
  return historicalWan26ModelOf(provider, model, videoDuration) ||
    (provider?.toLowerCase() === "dashscope" ? DASHSCOPE_USAGE_ALIASES.get(raw.toLowerCase()) || raw : raw);
};

type UsageModelRow = { service: string; provider: string; model: string; video_duration?: number | null };

export const usageModelOf = (row: UsageModelRow): string =>
  canonicalUsageModel(row.provider, row.model, row.service === "video" ? row.video_duration : undefined);

export const usageModelKeyOf = (row: UsageModelRow): string =>
  `${row.service}|${row.provider}|${usageModelOf(row)}`;

// Other families, and Wan 2.6 without a usable per-call length, still need request context.
export const isAmbiguousWanUsageModel = (
  provider: string | null | undefined,
  model: string | null | undefined,
  videoDuration?: number | null
): boolean =>
  provider?.toLowerCase() === "dashscope" && ["wan", "wan2.5", "wan2.6", "wan2.7"].includes((model || "").toLowerCase()) &&
  !historicalWan26ModelOf(provider, model, videoDuration);

/** Preserve date suffixes; used only to explain legacy overrides, never to migrate them. */
export const usageOverrideTargetOf = (key: string): string | undefined => {
  const [provider, model] = key.split("|");
  if (!provider || !model) return undefined;
  const [id, ...suffix] = model.split("@");
  const canonical = canonicalUsageModel(provider, id);
  return canonical === id ? undefined : `${provider}|${canonical}${suffix.length ? `@${suffix.join("@")}` : ""}`;
};
